export function plainTextToLexicalJson(text: string): string {
	const paragraphs = text.split(/\n{2,}/).filter(p => p.trim().length > 0)
	const children =
		paragraphs.length > 0
			? paragraphs.map(paragraph => {
					const lines = paragraph.split("\n")
					const lineNodes = lines.flatMap((line, i) => {
						const textNode = {
							detail: 0,
							format: 0,
							mode: "normal",
							style: "",
							text: line,
							type: "text",
							version: 1,
						}
						if (i < lines.length - 1) {
							return [textNode, { type: "linebreak", version: 1 }]
						}
						return [textNode]
					})
					return {
						children: lineNodes,
						direction: "ltr",
						format: "",
						indent: 0,
						type: "paragraph",
						version: 1,
					}
				})
			: [{ children: [], direction: null, format: "", indent: 0, type: "paragraph", version: 1 }]
	return JSON.stringify({ root: { children, direction: "ltr", format: "", indent: 0, type: "root", version: 1 } })
}

export function isLexicalJson(value: string): boolean {
	if (!value.startsWith("{")) return false
	try {
		const parsed = JSON.parse(value)
		return typeof parsed === "object" && parsed !== null && "root" in parsed && typeof parsed.root === "object"
	} catch {
		return false
	}
}

type SerializedLexicalNode = { type?: unknown; text?: unknown; children?: unknown }
function readSerializedLexicalNodeText(node: SerializedLexicalNode): string {
	if (node.type === "text" && typeof node.text === "string") return node.text
	if (node.type === "linebreak") return "\n"
	if (!Array.isArray(node.children)) return ""

	const childText = node.children.map(child =>
		typeof child === "object" && child !== null ? readSerializedLexicalNodeText(child) : "",
	)

	if (node.type === "root") return childText.join("\n\n")
	if (node.type === "list") return childText.join("\n")
	return childText.join("")
}

function getLexicalPlainText(value: string): string | null {
	if (!isLexicalJson(value)) return null

	try {
		const parsed = JSON.parse(value)
		if (typeof parsed !== "object" || parsed === null || !("root" in parsed)) return null
		const root = parsed.root
		if (typeof root !== "object" || root === null) return null
		return readSerializedLexicalNodeText(root)
	} catch {
		return null
	}
}

/** Landing page copy fields hold either plain text or Lexical JSON, depending on how they were written. */
export function getPlainOrLexicalText(value: string): string {
	return getLexicalPlainText(value) ?? value
}

export function getPlainOrLexicalTextCharacterCount(value: string): number {
	return getPlainOrLexicalText(value).length
}
